import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';

import { useWithdrawSteps } from './hooks/useWithdrawSteps';

import WalletCard from './components/WalletCard/WalletCard';
import ConfirmationStep from './contents/ConfirmationStep/ConfirmationStep';
import WithdrawStatusStep from './contents/WithdrawStatusStep/WithdrawStatusStep';

import AddBankAccountModal from './AddBankAccountModal';
import AddCryptoAccountModal from './AddCryptoAccountModal';

import { useWallet } from '../../context/WalletContext/WalletContext';

import {
  actionsRowSx,
  closeButtonSx,
  headerRowSx,
  innerFrameSx,
  modalCardSx,
  modalContainerSx,
  primaryButtonSx,
  titleSx,
} from './styles';

export interface WithdrawModalProps {
  open: boolean;
  onClose: (wasSuccessful?: boolean) => void;
}

interface WithdrawModalContentProps {
  onClose: (wasSuccessful?: boolean) => void;
  withdrawableBalance: number;
  creditBalance: number;
  totalBalance: number;
  refetchWallet: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

const WithdrawModalContent: React.FC<WithdrawModalContentProps> = ({
  onClose,
  withdrawableBalance,
  creditBalance,
  totalBalance,
  refetchWallet,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation('withdraw');
  const [currentStep, setCurrentStep] = useState(0);
  const [showAddBankModal, setShowAddBankModal] = useState(false);
  const [showAddCryptoModal, setShowAddCryptoModal] = useState(false);
  const [externalPaymentId, setExternalPaymentId] = useState<string | null>(null);

  const handleSuccess = (msg: string) => {
    onSuccess(msg);
    refetchWallet();
  };

  const handleComplete = () => {
    onClose(true);
  };

  const handleBridgerPayClose = () => {
    // Go back to payment method step when widget is closed
    setCurrentStep(1);
  };

  const {
    steps,
    amount,
    paymentMethod,
    selectedBankAccount,
    selectedCryptoAccount,
    selectedTradingAccountId,
    submitWithdrawal,
    refreshBanks,
    refreshCrypto,
    resetForm,
    bridgerPayLoading,
    initiateCardWithdrawal,
  } = useWithdrawSteps({
    withdrawableBalance: withdrawableBalance,
    onSuccess: handleSuccess,
    onError: onError,
    onComplete: handleComplete,
    onBridgerPayClose: handleBridgerPayClose,
    setOpenAddBankModal: setShowAddBankModal,
    setOpenAddCryptoModal: setShowAddCryptoModal,
    onCloseModal: () => onClose(false),
  });

  useEffect(() => {
    setCurrentStep(0);
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstStep = currentStep === 0;
  const isConfirmationStep = currentStep === steps.length;
  // BridgerPay step is step 2 when CARD is selected (Amount=0, Method=1, BridgerPay=2)
  const isBridgerPayStep = paymentMethod === 'CARD' && currentStep === 2;

  const handleNext = async () => {
    if (currentStep < steps.length) {
      const { validate } = steps[currentStep];
      if (validate && !validate()) return;
    }

    // If CARD is selected and we're on the payment method step (step 1),
    // initiate BridgerPay session first, only advance if successful
    if (paymentMethod === 'CARD' && currentStep === 1) {
      const sessionOk = await initiateCardWithdrawal();
      if (sessionOk) {
        setCurrentStep(2);
      }
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleConfirm = async () => {
    // For non-CARD methods, submit withdrawal and show status
    // CARD payments are handled by BridgerPay widget step directly
    const response = await submitWithdrawal();
    if (response?.externalPaymentId) {
      setExternalPaymentId(response.externalPaymentId);
    }
  };

  const handleCloseInternal = () => {
    onClose(false);
  };

  return (
    <>
      <Stack sx={innerFrameSx}>
        <Stack sx={headerRowSx}>
          <Typography sx={titleSx}>{t('MODAL.TITLE')}</Typography>
          <IconButton
            sx={closeButtonSx}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseInternal();
            }}
          >
            <X size={20} color="#CCD4FF" />
          </IconButton>
        </Stack>

        <WalletCard
          totalBalance={totalBalance}
          withdrawableBalance={withdrawableBalance}
          creditBalance={creditBalance}
          onClose={handleCloseInternal}
        />

        {externalPaymentId ? (
          <WithdrawStatusStep
            transactionExternalId={externalPaymentId}
            onSuccess={() => {
              refetchWallet();
            }}
          />
        ) : currentStep < steps.length ? (
          steps[currentStep].render()
        ) : (
          <ConfirmationStep
            amount={parseFloat(amount) || 0}
            method={
              paymentMethod === 'BANK' ? 'Bank Transfer'
              : paymentMethod === 'CARD' ? 'Credit Card'
              : paymentMethod === 'TRADING_ACCOUNT' ? 'RaiseFX'
              : 'Cryptocurrency'
            }
            account={
              paymentMethod === 'BANK'
                ? (selectedBankAccount ? `${selectedBankAccount.bankName} (...${selectedBankAccount.iban.slice(-4)})` : 'N/A')
                : paymentMethod === 'CARD'
                ? 'Credit Card (BridgerPay)'
                : paymentMethod === 'TRADING_ACCOUNT'
                ? (selectedTradingAccountId ? `Trading Account #${selectedTradingAccountId}` : 'N/A')
                : (selectedCryptoAccount ? `${selectedCryptoAccount.cryptoNetwork} (...${selectedCryptoAccount.cryptoAddress.slice(0,4)})` : 'N/A')
            }
            receiveAmount={parseFloat(amount) || 0}
          />
        )}

        {/* Hide buttons on BridgerPay step and when status is shown */}
        {!isBridgerPayStep && !externalPaymentId && (
          <Stack sx={actionsRowSx}>
            {!isFirstStep && (
              <Button variant="outlined" fullWidth onClick={handleBack}>
                {t('MODAL.BACK')}
              </Button>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={isConfirmationStep ? handleConfirm : handleNext}
              disabled={
                bridgerPayLoading ||
                (!isConfirmationStep && currentStep < steps.length && !steps[currentStep].isValid())
              }
              sx={primaryButtonSx}
            >
              {bridgerPayLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isConfirmationStep ? (
                t('CONFIRMATION.TITLE')
              ) : (
                t('MODAL.CONTINUE')
              )}
            </Button>
          </Stack>
        )}
      </Stack>

      <AddBankAccountModal
        open={showAddBankModal}
        onClose={() => setShowAddBankModal(false)}
        onSuccess={() => {
          refreshBanks();
          handleSuccess(t('MODAL.BANK_ACCOUNT_ADDED'));
        }}
      />

      <AddCryptoAccountModal
        open={showAddCryptoModal}
        onClose={() => setShowAddCryptoModal(false)}
        onSuccess={() => {
          refreshCrypto();
          handleSuccess(t('MODAL.CRYPTO_ACCOUNT_ADDED'));
        }}
      />
    </>
  );
};

const WithdrawModal: React.FC<WithdrawModalProps> = ({
  open,
  onClose,
}) => {
  const { wallet, refetch: refetchWallet } = useWallet();

  const withdrawableBalance = wallet?.withdrawableWallet ?? 0;
  const creditBalance = wallet?.creditWallet ?? 0;
  const totalBalance = withdrawableBalance + creditBalance;

  useEffect(() => {
    if (open) {
      refetchWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseInternal = () => {
    onClose(false);
  };

  const handleSuccess = (msg: string) => {
    toast.success(msg, { position: toast.POSITION.TOP_RIGHT });
  };

  const handleError = (msg: string) => {
    toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
  };

  return (
    <Modal open={open} onClose={handleCloseInternal}>
      <Box sx={modalContainerSx} onClick={handleCloseInternal}>
        <Card sx={modalCardSx} onClick={(e) => e.stopPropagation()}>
          <Suspense fallback={
            <Stack sx={innerFrameSx} alignItems="center" justifyContent="center">
              <CircularProgress />
            </Stack>
          }>
            {open && (
              <WithdrawModalContent
                onClose={onClose}
                withdrawableBalance={withdrawableBalance}
                creditBalance={creditBalance}
                totalBalance={totalBalance}
                refetchWallet={refetchWallet}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            )}
          </Suspense>
        </Card>
      </Box>
    </Modal>
  );
};

export default WithdrawModal;
